/**
 * useSectionContextSync — reads the current URL to determine what section
 * the user is viewing, extracts relevant parameters, and syncs them
 * to the AiChatProvider's section context.
 *
 * This approach avoids modifying each builder component — instead we parse
 * the URL search params (which each builder already stores its state in)
 * and provide them as context to the AI.
 */
import { useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useSectionContext } from './AiChatProvider';

const SECTION_ROUTES: Record<string, string> = {
  '/product': 'product',
  '/marketing': 'marketing',
  '/event-flow': 'event_flow',
  '/screen-flow': 'screen_flow',
  '/users': 'users',
  '/purchases': 'purchases',
  '/push-notifications': 'push_notifications',
  '/activity': 'activity',
};

/**
 * Call this inside AppLayout to keep section context in sync
 * with the current route. No need to modify individual builders.
 */
export function useSectionContextSync() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { setSectionContext } = useSectionContext();

  const context = useMemo(() => {
    const path = location.pathname;

    // Find matching section by longest prefix
    let currentSection: string | null = null;
    for (const [route, section] of Object.entries(SECTION_ROUTES)) {
      if (path.startsWith(route)) {
        currentSection = section;
        break;
      }
    }

    if (!currentSection || path === '/ai-chat' || path.startsWith('/ai-chat/')) return null;

    // Extract URL state params into a clean object
    const params: Record<string, string | string[]> = {};
    searchParams.forEach((value, key) => {
      const existing = params[key];
      if (existing) {
        if (Array.isArray(existing)) {
          existing.push(value);
        } else {
          params[key] = [existing, value];
        }
      } else {
        params[key] = value;
      }
    });

    return {
      currentSection,
      subPath: path.replace(/^\/[^/]+/, ''),
      dateRange: {
        start: params.start || null,
        end: params.end || null,
        preset: params.preset || null,
      },
      filters: params.filters || null,
      ...extractSectionSpecificParams(currentSection, params),
    };
  }, [location.pathname, searchParams]);

  useEffect(() => {
    setSectionContext(context);
  }, [context, setSectionContext]);
}

function extractSectionSpecificParams(
  section: string,
  params: Record<string, string | string[]>,
): Record<string, unknown> {
  switch (section) {
    case 'marketing':
      return {
        reportType: params.chart || 'pivot',
        breakdown: params.breakdown || 'campaign_name',
        selectedMetrics: typeof params.metrics === 'string' ? params.metrics.split(',') : params.metrics,
        interval: params.interval,
        cumulative: params.cumulative === 'true',
      };
    case 'product':
      return {
        tableName: params.table,
        events: typeof params.events === 'string' ? params.events.split(',') : params.events,
        reportType: params.chart || 'time_series',
        interval: params.interval,
        countMethod: params.count,
        breakdown: params.breakdown,
      };
    case 'event_flow':
    case 'screen_flow':
      return {
        collapseRepeats: params.collapse !== 'false',
        startFromEvent: params.startFrom,
        kpiEvent: params.kpi,
        filterMode: params.filterMode,
      };
    case 'users':
    case 'purchases':
      return {
        page: params.page ? Number(params.page) : 1,
      };
    default:
      return {};
  }
}
